import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Globe, RefreshCw, ShieldCheck, AlertTriangle, ExternalLink,
  Loader2, CheckCircle2, XCircle, Info, ShoppingCart, Wallet, Trash2, Play, Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function severityClass(daysLeft) {
  if (daysLeft == null) return "bg-slate-100 text-slate-600";
  if (daysLeft < 0) return "bg-red-200 text-red-900 border-red-300 font-bold";
  if (daysLeft <= 7) return "bg-red-100 text-red-700 border-red-200";
  if (daysLeft <= 14) return "bg-orange-100 text-orange-700 border-orange-200";
  if (daysLeft <= 30) return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-emerald-100 text-emerald-700 border-emerald-200";
}

const LIFECYCLE_LABELS = {
  active: { label: "aktywna", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  expiring_soon: { label: "wygasa", className: "bg-amber-100 text-amber-700 border-amber-200" },
  expired_in_quarantine: { label: "KWARANTANNA", className: "bg-red-100 text-red-700 border-red-300 font-bold" },
  lost: { label: "UTRACONA", className: "bg-slate-300 text-slate-800 border-slate-400 font-bold line-through" },
  unknown: { label: "brak danych", className: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function AftermarketDomains() {
  const [domains, setDomains] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("");
  const [showAutoRenew, setShowAutoRenew] = useState(true);
  const [onlyEpidemos, setOnlyEpidemos] = useState(true);
  const [totals, setTotals] = useState({ in_aftermarket: 0, in_epidemos: 0 });
  const [account, setAccount] = useState(null);

  const [renewDialog, setRenewDialog] = useState(null); // {name, period, cost?, confirming}
  const [renewing, setRenewing] = useState(false);
  const [pending, setPending] = useState({ actions: [], pending_count: 0, executed_count: 0, total_pending_cost: 0, currency: "PLN" });
  const [executingAll, setExecutingAll] = useState(false);
  const [outages, setOutages] = useState({ outages: [], total: 0 });
  const [expandedOutage, setExpandedOutage] = useState(null);
  const [recheckingId, setRecheckingId] = useState(null);
  const [recheckResults, setRecheckResults] = useState({}); // site_id -> last result
  const [disablingId, setDisablingId] = useState(null);
  const [pubFailures, setPubFailures] = useState({ failures: [], total_alerts: 0, total_sites: 0 });
  const [expandedFailure, setExpandedFailure] = useState(null);
  const [resolvingFailures, setResolvingFailures] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/aftermarket/domains`, {
        params: { only_epidemos: onlyEpidemos },
      });
      setDomains(res.data.domains || []);
      setLastSync(res.data.last_sync);
      setTotals({
        in_aftermarket: res.data.total_in_aftermarket || 0,
        in_epidemos: res.data.total_in_epidemos || 0,
      });
    } catch (e) {
      toast.error("Blad pobierania listy: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [onlyEpidemos]);

  const loadAccount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/aftermarket/account`);
      if (res.data.ok) setAccount(res.data.balance);
    } catch (_) {}
  }, []);

  const loadPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/aftermarket/pending`);
      setPending(res.data);
    } catch (_) {}
  }, []);

  const loadOutages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/service-outages`);
      setOutages(res.data);
    } catch (_) {}
  }, []);

  const loadPubFailures = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/publication-failures`);
      setPubFailures(res.data);
    } catch (_) {}
  }, []);

  const resolvePublicationFailures = async (siteId, siteName) => {
    if (!window.confirm(`Oznaczyć wszystkie alerty publication_failed dla ${siteName} jako resolved? (przeczytałem i zajmę się)`)) return;
    setResolvingFailures(siteId);
    try {
      const res = await axios.patch(`${API}/monitor/alerts/bulk`, {
        error_type: "publication_failed",
        source_id: siteId,
        new_status: "resolved",
        notes: "Resolved manually from publication-failures panel"
      });
      toast.success(`✓ ${siteName}: ${res.data.modified}/${res.data.matched} alertów zaresolvowanych`);
      await loadPubFailures();
      setExpandedFailure(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setResolvingFailures(null);
    }
  };

  const disableSite = async (siteId, siteName) => {
    if (!siteId) return;
    const reason = window.prompt(
      `Wyłączyć portal "${siteName}"?\n\nPo wyłączeniu:\n• auto-publish zostanie wyłączony\n• monitor zdrowia przestanie sprawdzać\n• zaległe publikacje (error/scheduled) zostaną wstrzymane (paused)\n• wszystkie aktywne alerty awarii zostaną zaresolvowane\n\nPodaj powód (zostanie zapisany):`,
      "Domena wykupiona przez kogoś innego — czekam na wygaśnięcie"
    );
    if (reason === null) return; // cancel
    setDisablingId(siteId);
    try {
      const res = await axios.post(`${API}/admin/service-outages/${siteId}/disable`, { reason });
      if (res.data.ok) {
        toast.success(`✓ ${siteName} wyłączony. ${res.data.alerts_resolved} alertów zaresolvowanych, ${res.data.posts_paused} publikacji wstrzymanych.`);
        await loadOutages();
      }
    } catch (e) {
      toast.error(`Wyłączenie nieudane: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDisablingId(null);
    }
  };

  const recheckSite = async (siteId, siteName) => {
    if (!siteId) return;
    setRecheckingId(siteId);
    try {
      const res = await axios.post(`${API}/admin/service-outages/${siteId}/recheck`);
      setRecheckResults((prev) => ({ ...prev, [siteId]: res.data }));
      if (res.data.healthy) {
        if (res.data.pending_publications > 0) {
          toast.success(`✓ ${siteName} działa! Wznowiono ${res.data.resumed_in_background} publikacji w tle.`);
        } else {
          toast.success(`✓ ${siteName} działa! Brak zaległych publikacji.`);
        }
        // Odśwież OBIE listy — portal mogł zniknąć z outages i pub-failures
        await Promise.all([loadOutages(), loadPubFailures()]);
        // Zwiń panel jak portal zniknął
        setExpandedOutage(null);
        setExpandedFailure(null);
      } else {
        toast.error(`✗ ${siteName} nadal nie działa: ${res.data.error || `HTTP ${res.data.status}`}`);
      }
    } catch (e) {
      toast.error(`Recheck nieudany: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRecheckingId(null);
    }
  };

  useEffect(() => {
    load();
    loadAccount();
    loadPending();
    loadOutages();
    loadPubFailures();
  }, [load, loadAccount, loadPending, loadOutages, loadPubFailures]);

  // jezeli URL ma #outages / #publication-failures -> autoscroll
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      const el = document.getElementById(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [outages, pubFailures]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/aftermarket/sync`);
      if (res.data.error) {
        toast.error("Sync nieudany: " + res.data.error);
      } else {
        toast.success(`Synced ${res.data.synced || 0} domen (wygasajacych <=30 dni: ${res.data.expiring_30d || 0})`);
        await load();
        await loadAccount();
      }
    } catch (e) {
      toast.error("Sync nieudany: " + (e.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const openRenew = async (name) => {
    setRenewDialog({ name, period: 1, cost: null, loading: true });
    try {
      const res = await axios.get(`${API}/aftermarket/check-renew/${encodeURIComponent(name)}?period=1`);
      setRenewDialog({ name, period: 1, cost: res.data.ok ? res.data.cost : null, error: res.data.error });
    } catch (e) {
      setRenewDialog({ name, period: 1, cost: null, error: e.message });
    }
  };

  const reloadCost = async (period) => {
    if (!renewDialog) return;
    setRenewDialog({ ...renewDialog, period, cost: null, loading: true });
    try {
      const res = await axios.get(`${API}/aftermarket/check-renew/${encodeURIComponent(renewDialog.name)}?period=${period}`);
      setRenewDialog({ ...renewDialog, period, cost: res.data.ok ? res.data.cost : null, error: res.data.error });
    } catch (e) {
      setRenewDialog({ ...renewDialog, period, cost: null, error: e.message });
    }
  };

  const confirmRenew = async () => {
    if (!renewDialog) return;
    setRenewing(true);
    try {
      const res = await axios.post(`${API}/aftermarket/renew`, { name: renewDialog.name, period: renewDialog.period });
      if (res.data.ok && res.data.executed) {
        toast.success(`Odnowiono ${renewDialog.name} (${renewDialog.period} rok)`);
      } else if (res.data.ok && res.data.queued) {
        toast.info(
          (res.data.balance_error
            ? `${renewDialog.name} dodano do kolejki — brak środków na koncie Aftermarket. Doładuj i kliknij "Wykonaj wszystkie".`
            : `${renewDialog.name} dodano do kolejki, ale wykonanie się nie powiodło: ${res.data.error || "?"}`),
          { duration: 6000 }
        );
      } else {
        toast.error("Nie udalo sie odnowic: " + (res.data.error || res.data.status || "nieznany blad"));
      }
      setRenewDialog(null);
      await load();
      await loadPending();
    } catch (e) {
      toast.error("Blad: " + (e.response?.data?.detail || e.message));
    } finally {
      setRenewing(false);
    }
  };

  const executeAllPending = async () => {
    setExecutingAll(true);
    try {
      const res = await axios.post(`${API}/aftermarket/pending/execute-all`);
      const { executed, still_pending, total, errors } = res.data;
      if (executed === total) {
        toast.success(`Wykonano wszystkie ${executed} zaplanowane odnowienia!`);
      } else if (executed > 0) {
        toast.warning(`${executed}/${total} wykonane, ${still_pending} dalej w kolejce${errors?.length ? ` (błędy: ${errors.length})` : ""}`);
      } else {
        toast.error(`Nie udało się wykonać żadnego (${total} prób). Sprawdź saldo lub błędy.`);
      }
      await loadPending();
      await load();
      await loadAccount();
    } catch (e) {
      toast.error("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setExecutingAll(false);
    }
  };

  const executeOnePending = async (id) => {
    try {
      const res = await axios.post(`${API}/aftermarket/pending/${id}/execute`);
      if (res.data.status === "executed") {
        toast.success(`Wykonano: ${res.data.name}`);
      } else if (res.data.status === "pending") {
        toast.warning(`${res.data.name}: ${res.data.is_balance_error ? "brak środków" : "błąd, sprawdź"}`);
      } else {
        toast.error(`${res.data.name || id}: ${res.data.status}`);
      }
      await loadPending();
      await load();
      await loadAccount();
    } catch (e) {
      toast.error("Błąd: " + e.message);
    }
  };

  const removePending = async (id, name) => {
    if (!window.confirm(`Usunąć ${name} z kolejki?`)) return;
    try {
      await axios.delete(`${API}/aftermarket/pending/${id}`);
      toast.success(`Usunięto ${name} z kolejki`);
      await loadPending();
    } catch (e) {
      toast.error("Błąd: " + e.message);
    }
  };

  const filtered = domains.filter((d) => {
    if (!showAutoRenew && d.autorenew === true) return false;
    if (!filter) return true;
    return (d.name || "").toLowerCase().includes(filter.toLowerCase());
  });

  const stats = {
    total: domains.length,
    autoRenew: domains.filter((d) => d.autorenew === true).length,
    expiringSoon: domains.filter((d) => d.lifecycle_state === "expiring_soon" && !d.autorenew).length,
    inQuarantine: domains.filter((d) => d.lifecycle_state === "expired_in_quarantine").length,
    lost: domains.filter((d) => d.lifecycle_state === "lost").length,
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Globe className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold">Domeny (Aftermarket.pl)</h1>
            <p className="text-xs text-muted-foreground">
              Ostatni sync: {lastSync ? new Date(lastSync).toLocaleString("pl-PL") : "nigdy"}
              {" · "}
              <span className="text-blue-600 font-medium">{totals.in_epidemos}</span> portali Epidemos w portfolio aftermarket <span className="text-slate-400">({totals.in_aftermarket} total)</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs flex items-center gap-1.5 px-2 py-1 rounded border bg-slate-50">
            <input
              type="checkbox"
              checked={onlyEpidemos}
              onChange={(e) => setOnlyEpidemos(e.target.checked)}
            />
            Tylko portale Epidemos
          </label>
          <Button onClick={sync} disabled={syncing} size="sm">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Synchronizuj teraz
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Wszystkie</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Auto-renew</p>
          <p className="text-2xl font-bold text-emerald-600">{stats.autoRenew}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Wygasa &lt;30d</p>
          <p className="text-2xl font-bold text-amber-600">{stats.expiringSoon}</p>
        </CardContent></Card>
        <Card className="border-red-200 bg-red-50/40"><CardContent className="p-4">
          <p className="text-xs text-red-700 flex items-center gap-1 font-semibold">
            <AlertTriangle className="w-3 h-3" /> KWARANTANNA
          </p>
          <p className="text-2xl font-bold text-red-700">{stats.inQuarantine}</p>
          <p className="text-[10px] text-red-600">grace period 30d</p>
        </CardContent></Card>
        <Card className="border-slate-300 bg-slate-100/60"><CardContent className="p-4">
          <p className="text-xs text-slate-600 flex items-center gap-1">
            <XCircle className="w-3 h-3" /> Utracone
          </p>
          <p className="text-2xl font-bold text-slate-700">{stats.lost}</p>
        </CardContent></Card>
      </div>

      {account && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-3 flex items-center gap-2 text-sm">
            <Wallet className="w-4 h-4 text-blue-600" />
            Saldo Aftermarket: <strong>{typeof account === "number" ? `${account.toFixed(2)} PLN` : JSON.stringify(account)}</strong>
            {pending.total_pending_cost > 0 && typeof account === "number" && (
              <>
                <span className="text-muted-foreground mx-2">·</span>
                {account >= pending.total_pending_cost ? (
                  <span className="text-emerald-700 text-xs">✓ wystarczy na kolejkę ({pending.total_pending_cost.toFixed(2)} {pending.currency})</span>
                ) : (
                  <span className="text-red-700 text-xs font-medium">
                    Brakuje {(pending.total_pending_cost - account).toFixed(2)} {pending.currency} na kolejkę
                  </span>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {outages.total > 0 && (
        <Card id="outages" className="border-red-300 bg-red-50/40">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2 text-red-900">
              <AlertTriangle className="w-4 h-4" />
              Awarie portali — {outages.total} nie działa
              <span className="text-xs font-normal text-red-700/80">(kliknij portal aby zobaczyć szczegóły i sugestie)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-red-100">
              {outages.outages.map((o) => {
                const expanded = expandedOutage === (o.alert_id || o.site_id);
                const isQuarantine = o.aftermarket?.lifecycle_state === "expired_in_quarantine";
                const isExpiring = o.aftermarket?.lifecycle_state === "expiring_soon";
                return (
                  <div key={o.alert_id || o.site_id} className="bg-white/40">
                    <button
                      type="button"
                      onClick={() => setExpandedOutage(expanded ? null : (o.alert_id || o.site_id))}
                      className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-red-100/40 text-left"
                    >
                      <span className="text-red-600">{expanded ? "▼" : "▶"}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{o.site_name || o.domain}</span>
                          {isQuarantine && (
                            <Badge className="text-[10px] bg-red-200 text-red-900 border-red-300">
                              🚨 KWARANTANNA
                            </Badge>
                          )}
                          {isExpiring && (
                            <Badge className="text-[10px] bg-amber-200 text-amber-900 border-amber-300">
                              wygasa &lt;30 dni
                            </Badge>
                          )}
                          {o.status_code && (
                            <Badge variant="outline" className="text-[10px]">HTTP {o.status_code}</Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-600 truncate">{o.url}</div>
                      </div>
                      <span className="text-xs text-red-700 truncate max-w-[300px]">{o.details_error || o.error_message}</span>
                    </button>
                    {expanded && (
                      <div className="px-8 pb-3 text-sm space-y-2 bg-red-50/60">
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <span className="text-slate-500">Ostatnie sprawdzenie:</span> {o.last_check_at?.slice(0,19) || "?"}
                          </div>
                          <div>
                            <span className="text-slate-500">Status HTTP:</span> {o.status_code || "—"}
                          </div>
                          <div className="col-span-2">
                            <span className="text-slate-500">Pełny błąd:</span> <code className="bg-slate-100 px-1 py-0.5 rounded text-[11px]">{o.details_error || o.error_message}</code>
                          </div>
                        </div>

                        {o.suggestions && o.suggestions.length > 0 && (
                          <div className="mt-2">
                            <div className="text-xs font-semibold text-slate-700 mb-1">💡 Sugerowane przyczyny / akcje:</div>
                            <ul className="space-y-1">
                              {o.suggestions.map((s, i) => (
                                <li key={i} className={`text-xs px-2 py-1 rounded border ${
                                  s.severity === "critical" ? "bg-red-100 border-red-300 text-red-900" :
                                  s.severity === "high" ? "bg-orange-100 border-orange-300 text-orange-900" :
                                  "bg-yellow-50 border-yellow-300 text-yellow-900"
                                }`}>{s.label}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {o.aftermarket?.in_portfolio && (
                          <div className="mt-2 p-2 rounded border border-blue-200 bg-blue-50 text-xs">
                            <div className="font-semibold text-blue-900 mb-1">🔗 Status w Aftermarket:</div>
                            <div className="grid grid-cols-2 gap-1 text-blue-800">
                              <div>Stan: <strong>{o.aftermarket.lifecycle_state}</strong></div>
                              <div>Dni do wygaśnięcia: <strong>{o.aftermarket.days_left ?? "—"}</strong></div>
                              {o.aftermarket.quarantine_days_left != null && (
                                <div>Dni do dropu: <strong className="text-red-700">{o.aftermarket.quarantine_days_left}</strong></div>
                              )}
                              <div>Auto-renew: <strong>{o.aftermarket.autorenew ? "✓ TAK" : "✗ NIE"}</strong></div>
                              {o.aftermarket.renewal_price && (
                                <div>Cena odnowienia: <strong>{o.aftermarket.renewal_price} {o.aftermarket.renewal_price_currency}</strong></div>
                              )}
                            </div>
                            {(isQuarantine || isExpiring) && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openRenew(o.domain); }}
                                className="mt-2 px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium"
                              >
                                {isQuarantine ? "🔓 Reaktywuj domenę" : "🔄 Odnów domenę"}
                              </button>
                            )}
                          </div>
                        )}

                        <div className="flex gap-2 mt-2 flex-wrap items-center">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); recheckSite(o.site_id, o.site_name || o.domain); }}
                            disabled={recheckingId === o.site_id}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded text-xs font-medium flex items-center gap-1"
                          >
                            {recheckingId === o.site_id ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Sprawdzam…</>
                            ) : (
                              <>🔄 Sprawdź ponownie</>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); disableSite(o.site_id, o.site_name || o.domain); }}
                            disabled={disablingId === o.site_id}
                            className="px-3 py-1 bg-slate-600 hover:bg-slate-700 disabled:bg-slate-300 text-white rounded text-xs font-medium flex items-center gap-1"
                            title="Wyłącz portal — wstrzyma auto-publish + monitoring + zaległe posty"
                          >
                            {disablingId === o.site_id ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Wyłączam…</>
                            ) : (
                              <>🚫 Wyłącz portal</>
                            )}
                          </button>
                          {recheckResults[o.site_id] && (
                            <span className={`text-xs px-2 py-1 rounded ${
                              recheckResults[o.site_id].healthy
                                ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                : "bg-red-100 text-red-800 border border-red-300"
                            }`}>
                              {recheckResults[o.site_id].healthy
                                ? `✓ Działa (HTTP ${recheckResults[o.site_id].status}, ${recheckResults[o.site_id].response_time_ms}ms)${recheckResults[o.site_id].resumed_in_background > 0 ? ` · wznowiono ${recheckResults[o.site_id].resumed_in_background} publikacji` : ""}`
                                : `✗ ${recheckResults[o.site_id].error || "HTTP " + recheckResults[o.site_id].status}`}
                            </span>
                          )}
                          {o.url && (
                            <>
                              <a
                                href={o.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-700 hover:underline"
                              >
                                🌐 Otwórz w przeglądarce →
                              </a>
                              <a
                                href={`/ai-monitor?source_id=${o.site_id}`}
                                className="text-xs text-blue-700 hover:underline"
                              >
                                📋 Historia alertów →
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {pubFailures.total_sites > 0 && (
        <Card id="publication-failures" className="border-orange-300 bg-orange-50/40">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2 text-orange-900">
              <AlertTriangle className="w-4 h-4" />
              Fail publikacji — {pubFailures.total_sites} portali, {pubFailures.total_alerts} alertów
              <span className="text-xs font-normal text-orange-700/80">(agent nie mógł opublikować na tych portalach)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-orange-100">
              {pubFailures.failures.map((f) => {
                const expanded = expandedFailure === f.site_id;
                return (
                  <div key={f.site_id} className="bg-white/40">
                    <button
                      type="button"
                      onClick={() => setExpandedFailure(expanded ? null : f.site_id)}
                      className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-orange-100/40 text-left"
                    >
                      <span className="text-orange-600">{expanded ? "▼" : "▶"}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{f.site_name}</span>
                          <Badge className="text-[10px] bg-orange-200 text-orange-900">
                            {f.fail_count} fail{f.fail_count > 1 ? "ów" : ""}
                          </Badge>
                          {f.auto_publish ? null : <Badge variant="outline" className="text-[10px]">stand-by</Badge>}
                          {f.disabled_at && <Badge className="text-[10px] bg-slate-200">wyłączony</Badge>}
                        </div>
                        <div className="text-xs text-slate-600 truncate">{f.url || f.site_id}</div>
                      </div>
                      <span className="text-xs text-orange-700 truncate max-w-[400px]">{f.last_message}</span>
                    </button>
                    {expanded && (
                      <div className="px-8 pb-3 text-sm space-y-2 bg-orange-50/60">
                        <div className="text-xs">
                          <span className="text-slate-500">Ostatni fail:</span> {f.last_failure_at?.slice(0,19) || "?"}
                        </div>

                        {f.suggestions && f.suggestions.length > 0 && (
                          <div>
                            <div className="text-xs font-semibold text-slate-700 mb-1">💡 Sugestie:</div>
                            <ul className="space-y-1">
                              {f.suggestions.map((s, i) => (
                                <li key={i} className={`text-xs px-2 py-1 rounded border ${
                                  s.severity === "critical" ? "bg-red-100 border-red-300 text-red-900" :
                                  s.severity === "high" ? "bg-orange-100 border-orange-300 text-orange-900" :
                                  "bg-yellow-50 border-yellow-300 text-yellow-900"
                                }`}>{s.label}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {f.alerts_sample && f.alerts_sample.length > 0 && (
                          <div className="mt-2">
                            <div className="text-xs font-semibold text-slate-700 mb-1">📋 Ostatnie {f.alerts_sample.length} alertów:</div>
                            <div className="space-y-1">
                              {f.alerts_sample.map((a) => (
                                <div key={a.id} className="text-xs p-2 bg-white border border-orange-200 rounded">
                                  <div className="text-slate-500">{a.created_at?.slice(0,19)}</div>
                                  <div>{a.message}</div>
                                  {a.details?.title && (
                                    <div className="text-slate-600 mt-1">📄 Post: <em>{a.details.title}</em></div>
                                  )}
                                  {a.details?.wp_status && (
                                    <div className="text-slate-600">Status: HTTP {a.details.wp_status}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {f.stuck_posts && f.stuck_posts.length > 0 && (
                          <div className="mt-2">
                            <div className="text-xs font-semibold text-slate-700 mb-1">📌 Posty zatrzymane na status=error ({f.stuck_posts.length}):</div>
                            <div className="space-y-1">
                              {f.stuck_posts.map((p) => (
                                <div key={p.id} className="text-xs p-1.5 bg-slate-50 border border-slate-200 rounded">
                                  <strong>{p.title}</strong> · retry_count={p.retry_count || 0}
                                  <div className="text-slate-500">{p.publish_error}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2 mt-2 flex-wrap items-center">
                          {f.site_id && f.site_id !== "(brak)" && (
                            <button
                              type="button"
                              onClick={() => recheckSite(f.site_id, f.site_name)}
                              disabled={recheckingId === f.site_id}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded text-xs"
                            >
                              {recheckingId === f.site_id ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "🔄 Sprawdź ponownie i ponów"}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => resolvePublicationFailures(f.site_id, f.site_name)}
                            disabled={resolvingFailures === f.site_id}
                            className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white rounded text-xs"
                          >
                            {resolvingFailures === f.site_id ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "✓ Oznacz jako resolved"}
                          </button>
                          {f.url && (
                            <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 hover:underline">
                              🌐 Otwórz portal →
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {pending.pending_count > 0 && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardHeader className="pb-2 pt-3 px-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm flex items-center gap-2 text-amber-900">
                <ShoppingCart className="w-4 h-4" />
                Zaplanowane do opłacenia ({pending.pending_count})
                {pending.total_pending_cost > 0 && (
                  <Badge className="bg-amber-200 text-amber-900 border border-amber-300">
                    Razem: {pending.total_pending_cost.toFixed(2)} {pending.currency}
                  </Badge>
                )}
              </CardTitle>
              {pending.pending_count > 0 && (
                <Button
                  size="sm"
                  onClick={executeAllPending}
                  disabled={executingAll}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {executingAll ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                  Wykonaj wszystkie
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {pending.actions.map((a) => (
                <div
                  key={a.id}
                  className={`px-4 py-2 flex items-center gap-3 text-sm ${
                    a.status === "executed" ? "bg-emerald-50/50 opacity-70" : "hover:bg-amber-100/40"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{a.name}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {a.action} · {a.period} rok
                      </Badge>
                      {a.status === "pending" && (
                        <Badge className="text-[10px] bg-amber-200 text-amber-900 border-amber-300">
                          <Clock className="w-3 h-3 mr-1" /> pending
                        </Badge>
                      )}
                      {a.status === "executed" && (
                        <Badge className="text-[10px] bg-emerald-200 text-emerald-900 border-emerald-300">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> wykonano
                        </Badge>
                      )}
                      {a.cached_price && (
                        <span className="text-xs text-muted-foreground">
                          {(a.cached_price * (a.period || 1)).toFixed(2)} {a.cached_price_currency || "PLN"}
                        </span>
                      )}
                    </div>
                    {a.last_error && a.status === "pending" && (
                      <p className="text-xs text-red-600 mt-0.5">
                        {a.last_error_is_balance ? "⚠ Brak środków — doładuj konto" : `Błąd: ${a.last_error.substring(0, 100)}`}
                      </p>
                    )}
                  </div>
                  {a.status === "pending" && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => executeOnePending(a.id)} title="Wykonaj teraz">
                        <Play className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removePending(a.id, a.name)} title="Usuń z kolejki">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                  {a.status === "executed" && (
                    <Button size="sm" variant="ghost" onClick={() => removePending(a.id, a.name)} title="Usuń z historii">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">Lista domen ({filtered.length})</CardTitle>
            <div className="flex items-center gap-2">
              <label className="text-xs flex items-center gap-1">
                <input type="checkbox" checked={showAutoRenew} onChange={(e) => setShowAutoRenew(e.target.checked)} />
                Pokaz z auto-renew
              </label>
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Szukaj domeny..."
                className="h-8 w-48"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              {domains.length === 0 ? "Brak danych. Wcisnij 'Synchronizuj teraz' zeby pobrac liste z Aftermarket.pl" : "Brak wynikow filtra"}
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((d) => {
                const lifecycle = LIFECYCLE_LABELS[d.lifecycle_state] || LIFECYCLE_LABELS.unknown;
                const inQuarantine = d.lifecycle_state === "expired_in_quarantine";
                const isLost = d.lifecycle_state === "lost";
                return (
                  <div
                    key={d.name}
                    className={`px-4 py-3 flex items-center gap-3 hover:bg-slate-50 ${
                      inQuarantine ? "bg-red-50/60 border-l-4 border-l-red-500" :
                      isLost ? "bg-slate-100 opacity-70" : ""
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{d.name}</span>
                        <Badge className={`text-[10px] border ${lifecycle.className}`}>
                          {lifecycle.label}
                        </Badge>
                        {d.autorenew === true && (
                          <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200">
                            <ShieldCheck className="w-3 h-3 mr-1" /> auto-renew
                          </Badge>
                        )}
                        {d.autorenew === false && !inQuarantine && !isLost && (
                          <Badge className="text-[10px] bg-red-100 text-red-700 border border-red-200">
                            <XCircle className="w-3 h-3 mr-1" /> bez auto-renew
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Wygasa: {d.expires_at ? new Date(d.expires_at).toLocaleDateString("pl-PL") : "—"}
                        {d.days_left != null && (
                          <span className={`ml-2 px-1.5 py-0.5 rounded border text-[10px] ${severityClass(d.days_left)}`}>
                            {d.days_left >= 0 ? `${d.days_left} dni` : `wygasla ${Math.abs(d.days_left)} dni temu`}
                          </span>
                        )}
                        {inQuarantine && d.quarantine_days_left != null && (
                          <span className="ml-2 px-1.5 py-0.5 rounded border text-[10px] bg-red-200 text-red-900 border-red-300 font-bold">
                            ⚠ {d.quarantine_days_left}d do utraty
                          </span>
                        )}
                        {d.deletion_at && (inQuarantine || isLost) && (
                          <span className="ml-2 text-[10px] text-red-700">
                            (drop: {new Date(d.deletion_at).toLocaleDateString("pl-PL")})
                          </span>
                        )}
                      </p>
                    </div>
                    {!isLost && (
                      <Button
                        size="sm"
                        variant={inQuarantine ? "default" : "outline"}
                        onClick={() => openRenew(d.name)}
                        className={`shrink-0 ${inQuarantine ? "bg-red-600 hover:bg-red-700" : ""}`}
                      >
                        <ShoppingCart className="w-3.5 h-3.5 mr-1" />
                        {inQuarantine ? "REAKTYWUJ" : "Odnów"}
                      </Button>
                    )}
                    {isLost && (
                      <span className="text-xs text-slate-500 italic">utracona</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!renewDialog} onOpenChange={(o) => !o && setRenewDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Odnowienie {renewDialog?.name}</DialogTitle>
            <DialogDescription>
              Operacja odpali sie w Aftermarket.pl. Sprawdz koszt zanim potwierdzisz.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Okres (lata):</label>
              <div className="flex gap-2 mt-1">
                {[1, 2, 3, 5].map((p) => (
                  <Button
                    key={p}
                    size="sm"
                    variant={renewDialog?.period === p ? "default" : "outline"}
                    onClick={() => reloadCost(p)}
                  >
                    {p}
                  </Button>
                ))}
              </div>
            </div>
            <div className="p-3 bg-slate-50 rounded-md text-sm">
              <p className="text-xs text-muted-foreground mb-1">Szacowany koszt:</p>
              <p className="font-mono text-sm">
                {renewDialog?.loading ? "ladowanie..." :
                 renewDialog?.error ? <span className="text-red-600">{renewDialog.error}</span> :
                 renewDialog?.cost ? JSON.stringify(renewDialog.cost) : "—"}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewDialog(null)}>Anuluj</Button>
            <Button onClick={confirmRenew} disabled={renewing} className="bg-red-600 hover:bg-red-700">
              {renewing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Potwierdz odnowienie
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
